import { useEffect, useRef } from "react";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_c9f69bc0-91ae-40a1-9da9-440da68b284c/artifacts/xyedl7ct_IMG_8766.png";

/**
 * HeroLogo — animated brand logo for the home hero.
 * Layers (back → front):
 *   1. radial halo (breathing scale + conic spin)
 *   2. expanding sonar rings (3 staggered)
 *   3. dashed orbit rings (counter-rotating)
 *   4. orbiting planes (2, different radii/speeds)
 *   5. sparkle particles
 *   6. logo card with mouse-tracked parallax tilt + float
 */
export default function HeroLogo() {
  const stageRef = useRef(null);
  const cardRef = useRef(null);

  useEffect(() => {
    const stage = stageRef.current;
    const card = cardRef.current;
    if (!stage || !card) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;

    let raf = 0;
    let targetX = 0;
    let targetY = 0;
    let curX = 0;
    let curY = 0;

    const onMove = (e) => {
      const r = stage.getBoundingClientRect();
      const px = (e.clientX - r.left) / r.width - 0.5;
      const py = (e.clientY - r.top) / r.height - 0.5;
      targetX = px * 14;
      targetY = py * -14;
    };
    const onLeave = () => {
      targetX = 0;
      targetY = 0;
    };
    const tick = () => {
      curX += (targetX - curX) * 0.08;
      curY += (targetY - curY) * 0.08;
      card.style.transform = `perspective(900px) rotateY(${curX}deg) rotateX(${curY}deg)`;
      raf = requestAnimationFrame(tick);
    };
    stage.addEventListener("mousemove", onMove);
    stage.addEventListener("mouseleave", onLeave);
    raf = requestAnimationFrame(tick);
    return () => {
      stage.removeEventListener("mousemove", onMove);
      stage.removeEventListener("mouseleave", onLeave);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div
      ref={stageRef}
      className="hero-logo-stage relative aspect-square max-w-[520px] ml-auto"
      data-testid="hero-logo-stage"
    >
      {/* Breathing halo */}
      <div className="absolute inset-[-4%] rounded-full hero-halo" />
      <div className="absolute inset-[-2%] rounded-full hero-halo-spin" />

      {/* Sonar pulse rings */}
      <span className="hero-pulse" style={{ animationDelay: "0s" }} />
      <span className="hero-pulse" style={{ animationDelay: "1.4s" }} />
      <span className="hero-pulse" style={{ animationDelay: "2.8s" }} />

      {/* Orbit dashed rings */}
      <div className="absolute inset-[2%] rounded-full hero-orbit-ring" />
      <div className="absolute inset-[-3%] rounded-full hero-orbit-ring reverse" />

      {/* Orbiting planes (use offset-path circles) */}
      <div className="hero-orbit hero-orbit-1">
        <span className="hero-plane">
          <svg viewBox="0 0 24 24" width="24" height="24">
            <path
              d="M21 16v-2l-8-5V3.5a1.5 1.5 0 1 0-3 0V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1L15 22v-1.5L13 19v-5.5l8 2.5z"
              fill="currentColor"
            />
          </svg>
        </span>
      </div>
      <div className="hero-orbit hero-orbit-2">
        <span className="hero-plane hero-plane-red">
          <svg viewBox="0 0 24 24" width="20" height="20">
            <path
              d="M21 16v-2l-8-5V3.5a1.5 1.5 0 1 0-3 0V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1L15 22v-1.5L13 19v-5.5l8 2.5z"
              fill="currentColor"
            />
          </svg>
        </span>
      </div>

      {/* Sparkles */}
      {[
        { top: "8%", left: "15%", d: 0 },
        { top: "20%", left: "85%", d: 0.6 },
        { top: "55%", left: "92%", d: 1.2 },
        { top: "82%", left: "70%", d: 1.8 },
        { top: "88%", left: "20%", d: 2.4 },
        { top: "45%", left: "4%", d: 3.0 },
        { top: "32%", left: "50%", d: 0.9 },
        { top: "70%", left: "45%", d: 2.1 },
      ].map((s, i) => (
        <span
          key={i}
          className="hero-sparkle"
          style={{ top: s.top, left: s.left, animationDelay: `${s.d}s` }}
        />
      ))}

      {/* Logo card — perspective + float */}
      <div className="absolute inset-[10%] hero-card-wrapper">
        <div ref={cardRef} className="hero-card-tilt">
          <div className="hero-card">
            <img
              src={LOGO_URL}
              alt="Bangladesh Travels & Tours AB"
              className="hero-logo-img logo-img"
              data-testid="hero-brand-logo"
            />
          </div>
        </div>
      </div>

      {/* Bottom city tag */}
      <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full bg-black/60 border border-white/10 backdrop-blur-md text-[10px] tracking-[0.3em] uppercase text-[color:var(--ivory-muted)] whitespace-nowrap">
        Dhaka · Stockholm
      </div>
    </div>
  );
}
